/* NYLA Virtual Try-On — UI + orchestration on top of the WebGL makeup engine.
 * Maybelline-style experience: live camera (or photo), product card with
 * Buy Now, shade carousel, per-shade intensity, draggable before/after
 * compare, capture & share. No frameworks, no build step.
 */
(function () {
  'use strict';

  // MediaPipe is loaded from the CDN on the storefront. Pinned to an exact
  // version: "latest" has broken the face landmarker before now.
  // The FILE has to be named: the bare package URL serves the CommonJS build,
  // which dies with "require is not defined" the moment it is import()ed.
  var TASKS_BASE = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.18';
  var TASKS_MODULE = TASKS_BASE + '/vision_bundle.mjs';
  var WASM_DIR = TASKS_BASE + '/wasm';
  var MODEL_URL = 'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task';

  var ICONS = {
    flip: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M20 7h-3l-2-2h-6L7 7H4a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1Z"/><path d="M9.5 13.5a2.5 2.5 0 0 1 4.3-1.7M14.5 13.5a2.5 2.5 0 0 1-4.3 1.7"/><path d="M14.6 10.2v1.6h-1.6M9.4 16.8v-1.6H11"/></svg>',
    photo: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.1-3.1a2 2 0 0 0-2.8 0L6 21"/></svg>',
    cam: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M20 7h-3l-2-2h-6L7 7H4a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1Z"/><circle cx="12" cy="13" r="3.2"/></svg>',
    reset: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 2.6-6.4"/><path d="M3 4v4h4"/></svg>',
    snap: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 16V4m0 12 4-4m-4 4-4-4"/><path d="M4 17v2a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-2"/></svg>',
    close: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6 6 18"/></svg>',
    none: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="12" cy="12" r="8.5"/><path d="M6.5 6.5l11 11"/></svg>',
    bag: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M6 7h12l1 13H5L6 7Z"/><path d="M9 9V6a3 3 0 0 1 6 0v3"/></svg>'
  };

  function el(tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }
  function fmtPct(v) { return Math.round(v * 100) + '%'; }

  // A shade's category is the tab it appears under; its layer is the part of
  // the face it paints. Lipstick and tint are different categories but the same
  // layer, so choosing one replaces the other — you cannot wear both at once.
  function layerOf(catOrShade) {
    var t = typeof catOrShade === 'string' ? catOrShade : (catOrShade && catOrShade.type);
    return t === 'blush' ? 'cheek' : 'lip';
  }
  // Track-fill % that matches the native thumb position (accounts for min/max,
  // so the filled line ends exactly under the circle).
  function fillPct(r) { var mn = +r.min || 0, mx = +r.max || 100; return (r.value - mn) / (mx - mn) * 100; }

  // ── Anonymous usage analytics (no cookies, no PII) ────────────────────────
  // Dormant on a Shopify theme: there is no back end to receive events, so no
  // stats config is ever supplied and track() returns immediately.
  var tracker = {
    url: null, on: false, q: [], t: null,
    cfg: function (stats) {
      if (stats && stats.on && stats.url && !this.url) { this.url = stats.url; this.on = true; }
    },
    sid: function () {
      try {
        var s = sessionStorage.getItem('nyt_sid');
        if (!s) { s = Math.random().toString(36).slice(2, 14); sessionStorage.setItem('nyt_sid', s); }
        return s;
      } catch (e) { return 'anon'; }
    },
    track: function (e, shade, meta) {
      if (!this.on) return;
      this.q.push({ e: e, s: shade || '', m: meta || '' });
      if (this.q.length >= 12) this.flush(); else this.schedule();
    },
    schedule: function () {
      var self = this;
      if (this.t) return;
      this.t = setTimeout(function () { self.t = null; self.flush(); }, 4000);
    },
    flush: function () {
      if (!this.q.length || !this.url) return;
      var fd = new FormData();
      fd.append('action', 'nyla_tryon_event');
      fd.append('sid', this.sid());
      fd.append('events', JSON.stringify(this.q.splice(0, 20)));
      try {
        if (navigator.sendBeacon) navigator.sendBeacon(this.url, fd);
        else fetch(this.url, { method: 'POST', body: fd, keepalive: true }).catch(function () {});
      } catch (e) {}
      if (this.q.length) this.schedule();
    }
  };
  document.addEventListener('visibilitychange', function () { if (document.hidden) tracker.flush(); });
  window.addEventListener('pagehide', function () { tracker.flush(); });

  /**
   * Loads the MediaPipe ES module.
   *
   * Shopify minifies theme assets with esbuild, which rewrites a literal
   * import() into require() — a function that does not exist in a browser, so
   * the engine dies with "require is not defined". Both routes below keep the
   * module URL inside a string, which the minifier cannot rewrite.
   */
  function loadVision(url) {
    try {
      // Fast path. Throws immediately under a policy without 'unsafe-eval'.
      return (new Function('u', 'return import(u);'))(url);
    } catch (e) {
      return moduleScriptImport(url);
    }
  }

  /** Same job without eval: let a module <script> do the importing. */
  function moduleScriptImport(url) {
    return new Promise(function (resolve, reject) {
      var key = '__nylaVision' + Math.random().toString(36).slice(2, 10);
      var settled = false;
      window.addEventListener(key, function () {
        settled = true;
        var mod = window[key];
        try { delete window[key]; } catch (e) { window[key] = undefined; }
        mod ? resolve(mod) : reject(new Error('tasks-vision loaded but was empty'));
      }, { once: true });

      var tag = document.createElement('script');
      tag.type = 'module';
      tag.textContent =
        'import * as v from ' + JSON.stringify(url) + ';' +
        'window[' + JSON.stringify(key) + ']=v;' +
        'window.dispatchEvent(new Event(' + JSON.stringify(key) + '));';
      tag.onerror = function () { if (!settled) reject(new Error('could not load tasks-vision')); };
      document.head.appendChild(tag);

      setTimeout(function () {
        if (!settled) reject(new Error('tasks-vision timed out'));
      }, 20000);
    });
  }

  // ── Shared face-landmarker (one per page, lazy) ───────────────────────────
  var landmarker = null, lmPromise = null, lmMode = 'VIDEO';
  function getLandmarker() {
    if (lmPromise) return lmPromise;
    lmPromise = loadVision(TASKS_MODULE).then(function (vision) {
      return vision.FilesetResolver.forVisionTasks(WASM_DIR).then(function (fileset) {
        var make = function (delegate) {
          return vision.FaceLandmarker.createFromOptions(fileset, {
            baseOptions: { modelAssetPath: MODEL_URL, delegate: delegate },
            runningMode: 'VIDEO', numFaces: 1
          });
        };
        return make('GPU').catch(function () { return make('CPU'); });
      });
    }).then(function (lm) { landmarker = lm; return lm; });
    return lmPromise;
  }
  function setLmMode(mode) {
    if (lmMode === mode) return Promise.resolve(landmarker);
    return landmarker.setOptions({ runningMode: mode }).then(function () { lmMode = mode; return landmarker; });
  }

  // ── Try-on instance ───────────────────────────────────────────────────────
  function TryOn(root) {
    this.root = root;
    var cfg = {};
    try { cfg = JSON.parse(root.getAttribute('data-config') || '{}'); } catch (e) {}
    this.cfg = cfg;
    tracker.cfg(cfg.stats);
    this.shades = cfg.shades || [];
    this.cat = cfg.type || 'lipstick';
    this.catLabels = cfg.catLabels || {};
    this.sel = { lip: null, cheek: null };
    this.lastTouched = null;
    this.defInt = { lip: (cfg.ui && cfg.ui.defLip || 80) / 100, cheek: (cfg.ui && cfg.ui.defBlush || 55) / 100 };
    this.intMem = this.loadIntensities();
    this.mode = 'camera';            // camera | photo
    this.facing = 'user';
    this.compare = false;
    this.divider = 0.5;
    this.fade = 0;
    this.smooth = null;
    this.running = false;
    this.stream = null;
    this.imageEl = null;
    this.imageLm = null;
    this.engine = null;
    this.lastT = 0;
    this.detectMs = 8;
    this.skip = false;

    this.build();
    this.bind();
    if (cfg.preselect) this.applyPreselect(cfg.preselect);
    this.renderChips();
    this.updateCard();
    this.updateIntensityUI();
  }

  TryOn.prototype.loadIntensities = function () {
    try { return JSON.parse(localStorage.getItem('nyla_tryon_int') || '{}'); } catch (e) { return {}; }
  };
  TryOn.prototype.saveIntensities = function () {
    try { localStorage.setItem('nyla_tryon_int', JSON.stringify(this.intMem)); } catch (e) {}
  };
  TryOn.prototype.intensityFor = function (shade) {
    if (this.intMem[shade.id] != null) return this.intMem[shade.id];
    var d = this.defInt[layerOf(shade)];
    return d != null ? d : 0.8;
  };

  // ── DOM ───────────────────────────────────────────────────────────────────
  TryOn.prototype.build = function () {
    var r = this.root;
    r.innerHTML = '';
    r.classList.add('nyt2');

    var stage = this.stage = el('div', 'nyt-stage');
    this.video = el('video', 'nyt-video');
    this.video.setAttribute('playsinline', '');
    this.video.muted = true; this.video.autoplay = true;
    this.canvas = el('canvas', 'nyt-canvas');
    stage.append(this.video, this.canvas);

    // top bar
    var top = el('div', 'nyt-topbar');
    var tl = el('div', 'nyt-top-group');
    this.btnFlip = el('button', 'nyt-icon nyt-flip', ICONS.flip); this.btnFlip.title = 'Flip camera';
    this.btnMode = el('button', 'nyt-icon nyt-modesw', ICONS.photo); this.btnMode.title = 'Try on a photo';
    tl.append(this.btnFlip, this.btnMode);
    var tr = el('div', 'nyt-top-group');
    this.btnReset = el('button', 'nyt-icon nyt-reset', ICONS.reset); this.btnReset.title = 'Reset';
    this.btnSnap = el('button', 'nyt-icon nyt-snap', ICONS.snap); this.btnSnap.title = 'Save photo';
    this.btnClose = el('button', 'nyt-icon nyt-x', ICONS.close); this.btnClose.title = 'Close';
    tr.append(this.btnReset, this.btnSnap, this.btnClose);
    top.append(tl, tr);
    stage.append(top);

    // compare divider
    this.cmpWrap = el('div', 'nyt-cmp');
    this.cmpWrap.innerHTML = '<div class="nyt-cmp-line"></div><div class="nyt-cmp-knob"><span>◂</span><span>▸</span></div><div class="nyt-cmp-tag nyt-cmp-l">BEFORE</div><div class="nyt-cmp-tag nyt-cmp-r">AFTER</div>';
    this.cmpWrap.hidden = true;
    stage.append(this.cmpWrap);

    // face hint
    this.hint = el('div', 'nyt-hint-chip', 'Move your face into the frame');
    this.hint.hidden = true;
    stage.append(this.hint);

    // product card
    this.card = el('div', 'nyt-pcard');
    this.card.hidden = true;
    stage.append(this.card);

    // overlays
    this.ovStart = el('div', 'nyt-overlay nyt-ov-start');
    this.ovStart.innerHTML =
      '<div class="nyt-brand">NYLA</div>' +
      '<div class="nyt-tag">VIRTUAL TRY-ON</div>' +
      '<button class="nyt-cta nyt-go-cam">' + ICONS.cam + '<span>Start camera</span></button>' +
      '<button class="nyt-cta nyt-cta-ghost nyt-go-photo">' + ICONS.photo + '<span>Upload a photo</span></button>' +
      '<p class="nyt-privacy">Everything runs on your device — photos and camera are never uploaded.</p>';
    this.ovLoad = el('div', 'nyt-overlay nyt-ov-load');
    this.ovLoad.innerHTML = '<div class="nyt-spin"></div><p class="nyt-load-msg">Setting up the experience…</p>';
    this.ovLoad.hidden = true;
    this.ovErr = el('div', 'nyt-overlay nyt-ov-err');
    this.ovErr.innerHTML = '<p class="nyt-err-msg"></p><div class="nyt-err-actions"><button class="nyt-cta nyt-retry"><span>Try again</span></button><button class="nyt-cta nyt-cta-ghost nyt-go-photo">' + ICONS.photo + '<span>Use a photo instead</span></button></div>';
    this.ovErr.hidden = true;
    stage.append(this.ovStart, this.ovLoad, this.ovErr);

    // bottom panel
    var panel = el('div', 'nyt-panel');
    this.catTabs = el('div', 'nyt-cats');
    panel.append(this.catTabs);

    var intRow = el('div', 'nyt-int-row');
    intRow.innerHTML = '<span class="nyt-int-label">Intensity</span><input type="range" class="nyt-int" min="10" max="100" value="80"><span class="nyt-int-val">80%</span>';
    this.intRow = intRow;
    panel.append(intRow);

    this.chips = el('div', 'nyt-chips');
    panel.append(this.chips);

    var bottom = el('div', 'nyt-bottom-tabs');
    this.tabShades = el('button', 'nyt-btab is-active', 'SHADES');
    this.tabCompare = el('button', 'nyt-btab', 'COMPARE');
    bottom.append(this.tabShades, this.tabCompare);
    panel.append(bottom);

    this.file = el('input', 'nyt-file');
    this.file.type = 'file'; this.file.accept = 'image/*'; this.file.hidden = true;

    r.append(stage, panel, this.file);

    var h = parseInt(this.cfg.height || 620, 10);
    r.style.setProperty('--nyt-h', h + 'px');
    if (!this.inModal()) this.btnClose.style.display = 'none';
  };

  TryOn.prototype.inModal = function () { return !!this.root.closest('.nyt-modal'); };

  // ── Events ────────────────────────────────────────────────────────────────
  TryOn.prototype.bind = function () {
    var self = this;
    var inlineOpen = function (meta) {
      if (!self.inModal() && !self._openTracked) { self._openTracked = true; tracker.track('open', '', meta); }
    };
    this.ovStart.querySelector('.nyt-go-cam').addEventListener('click', function () { inlineOpen('inline'); self.startCamera(); });
    this.root.querySelectorAll('.nyt-go-photo').forEach(function (b) {
      b.addEventListener('click', function () { inlineOpen('inline'); self.file.click(); });
    });
    this.ovErr.querySelector('.nyt-retry').addEventListener('click', function () { self.startCamera(); });
    this.file.addEventListener('change', function (e) {
      if (e.target.files && e.target.files[0]) self.loadPhoto(e.target.files[0]);
      e.target.value = '';
    });
    this.btnFlip.addEventListener('click', function () {
      self.facing = self.facing === 'user' ? 'environment' : 'user';
      self.startCamera();
    });
    this.btnMode.addEventListener('click', function () {
      if (self.mode === 'camera') self.file.click();
      else self.startCamera();
    });
    this.btnReset.addEventListener('click', function () { self.resetAll(); });
    this.btnSnap.addEventListener('click', function () { self.capture(); });
    this.btnClose.addEventListener('click', function () {
      var m = self.root.closest('.nyt-modal');
      if (m) closeModal(m);
    });

    // intensity
    this.slider = this.intRow.querySelector('.nyt-int');
    this.sliderVal = this.intRow.querySelector('.nyt-int-val');
    this.slider.addEventListener('input', function () {
      var v = self.slider.value / 100;
      var s = self.sel[layerOf(self.cat)];
      if (s) { self.intMem[s.id] = v; self.saveIntensities(); }
      self.sliderVal.textContent = self.slider.value + '%';
      self.slider.style.setProperty('--fill', fillPct(self.slider) + '%');
      self.pushProducts();
      self.repaintStatic();
    });

    // bottom tabs
    this.tabShades.addEventListener('click', function () { self.setCompare(false); });
    this.tabCompare.addEventListener('click', function () { tracker.track('compare_use'); self.setCompare(true); });

    // compare drag
    var knob = this.cmpWrap.querySelector('.nyt-cmp-knob');
    var drag = function (e) {
      var rect = self.stage.getBoundingClientRect();
      var x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
      self.divider = Math.min(0.92, Math.max(0.08, x / rect.width));
      self.applyDivider();
      e.preventDefault();
    };
    var up = function () {
      window.removeEventListener('pointermove', drag);
      window.removeEventListener('pointerup', up);
    };
    knob.addEventListener('pointerdown', function (e) {
      window.addEventListener('pointermove', drag);
      window.addEventListener('pointerup', up);
      e.preventDefault();
    });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden && self.mode === 'camera') self.pauseCamera();
    });
  };

  // ── State → engine ────────────────────────────────────────────────────────
  TryOn.prototype.pushProducts = function () {
    if (!this.engine) return;
    var lip = this.sel.lip, bl = this.sel.cheek;
    this.engine.setLip(lip ? { color: lip.hex, intensity: this.intensityFor(lip) } : null);
    this.engine.setBlush(bl ? { color: bl.hex, intensity: this.intensityFor(bl) } : null);
  };

  TryOn.prototype.applyDivider = function () {
    var x = this.compare ? this.divider : 0;
    if (this.engine) this.engine.setDivider(x);
    this.cmpWrap.style.setProperty('--x', (this.divider * 100) + '%');
    this.repaintStatic();
  };

  TryOn.prototype.setCompare = function (on) {
    this.compare = on;
    this.cmpWrap.hidden = !on;
    this.tabShades.classList.toggle('is-active', !on);
    this.tabCompare.classList.toggle('is-active', on);
    this.applyDivider();
  };

  TryOn.prototype.resetAll = function () {
    this.sel = { lip: null, cheek: null };
    this.lastTouched = null;
    this.setCompare(false);
    this.pushProducts();
    this.renderChips();
    this.updateCard();
    this.repaintStatic();
  };

  TryOn.prototype.applyPreselect = function (id) {
    var s = this.shades.find(function (x) { return x.id === id; });
    if (!s) return;
    this.cat = s.type;
    this.sel[layerOf(s)] = s;
    this.lastTouched = s;
  };

  // ── Category tabs + chips ─────────────────────────────────────────────────
  TryOn.prototype.cats = function () {
    var self = this;
    var DEFAULTS = { lipstick: 'LIPSTICK', tint: 'TINT', gloss: 'GLOSS', blush: 'BLUSH' };
    var order = [], seen = {};
    // Order follows the shade list, so whatever the merchant puts first wins.
    this.shades.forEach(function (s) {
      var t = s.type || 'lipstick';
      if (!seen[t]) { seen[t] = true; order.push(t); }
    });
    return order.map(function (t) {
      return [t, self.catLabels[t] || DEFAULTS[t] || t.toUpperCase()];
    });
  };

  TryOn.prototype.renderChips = function () {
    var self = this;
    // category tabs
    this.catTabs.innerHTML = '';
    this.cats().forEach(function (c) {
      var b = el('button', 'nyt-cat' + (self.cat === c[0] ? ' is-active' : ''), c[1]);
      b.addEventListener('click', function () {
        self.cat = c[0];
        self.renderChips();
        self.updateIntensityUI();
        self.updateCard(self.sel[layerOf(self.cat)] || null);
      });
      self.catTabs.append(b);
    });

    // chips
    this.chips.innerHTML = '';
    var none = el('button', 'nyt-chip nyt-chip-none' + (!this.sel[layerOf(this.cat)] ? ' is-sel' : ''), '<span class="nyt-chip-sw">' + ICONS.none + '</span><span class="nyt-chip-num">None</span>');
    none.addEventListener('click', function () {
      self.sel[layerOf(self.cat)] = null;
      self.lastTouched = null;
      self.pushProducts(); self.renderChips(); self.updateCard(); self.repaintStatic();
    });
    this.chips.append(none);

    this.shades.filter(function (s) { return s.type === self.cat; }).forEach(function (s) {
      var cur = self.sel[layerOf(self.cat)];
      var seld = cur && cur.id === s.id;
      var chip = el('button', 'nyt-chip' + (seld ? ' is-sel' : '') + (s.in_stock === false ? ' is-oos' : ''));
      chip.innerHTML = '<span class="nyt-chip-sw" style="background:' + s.hex + '"></span><span class="nyt-chip-num">' + (s.num || '') + '</span>';
      chip.title = s.name;
      chip.addEventListener('click', function () {
        self.sel[layerOf(self.cat)] = s;
        self.lastTouched = s;
        tracker.track('shade_try', s.id);
        self.pushProducts(); self.renderChips(); self.updateIntensityUI(); self.updateCard(s); self.repaintStatic();
        if (self.compare) self.setCompare(true);
      });
      self.chips.append(chip);
    });

    var sel = this.sel[layerOf(this.cat)];
    this.intRow.classList.toggle('is-off', !sel);
  };

  TryOn.prototype.updateIntensityUI = function () {
    var s = this.sel[layerOf(this.cat)];
    var v = s ? this.intensityFor(s) : this.defInt[layerOf(this.cat)];
    this.slider.value = Math.round(v * 100);
    this.sliderVal.textContent = Math.round(v * 100) + '%';
    this.slider.style.setProperty('--fill', fillPct(this.slider) + '%');
    this.intRow.classList.toggle('is-off', !s);
  };

  // ── Product card ──────────────────────────────────────────────────────────
  TryOn.prototype.updateCard = function (shade) {
    var self = this;
    var s = shade !== undefined ? shade : (this.lastTouched || this.sel[layerOf(this.cat)]);
    if (!s) { this.card.hidden = true; return; }
    this.card.hidden = false;

    var thumb = s.img
      ? '<img class="nyt-pc-img" src="' + s.img + '" alt="">'
      : '<span class="nyt-pc-img nyt-pc-sw" style="background:' + s.hex + '"></span>';
    var oos = s.in_stock === false;
    var price = s.price ? '<div class="nyt-pc-price">' + s.price + '</div>' : '';
    var badge = oos ? '<span class="nyt-pc-oos">Out of stock</span>' : '';

    this.card.innerHTML =
      thumb +
      '<div class="nyt-pc-info">' +
        '<div class="nyt-pc-name">' + (s.product || (layerOf(s) === 'cheek' ? 'NYLA Blush' : 'NYLA Lips')) + '</div>' +
        '<div class="nyt-pc-shade">' + s.name + ' ' + badge + '</div>' +
        price +
      '</div>' +
      '<div class="nyt-pc-actions">' +
        '<button class="nyt-pc-x" title="Remove">' + ICONS.close + '</button>' +
        '<button class="nyt-pc-buy"' + (oos ? ' disabled' : '') + '>' + (oos ? 'SOLD OUT' : 'BUY NOW') + '</button>' +
      '</div>';

    this.card.querySelector('.nyt-pc-x').addEventListener('click', function () {
      self.sel[layerOf(s)] = null;
      if (self.lastTouched && self.lastTouched.id === s.id) self.lastTouched = null;
      self.pushProducts(); self.renderChips(); self.updateCard(); self.repaintStatic();
    });
    var buy = this.card.querySelector('.nyt-pc-buy');
    if (!oos) buy.addEventListener('click', function () { self.buyNow(s, buy); });
  };

  TryOn.prototype.buyNow = function (s, btn) {
    var cfg = this.cfg;
    var cart = cfg.cart || {};
    tracker.track('buy_click', s.id);

    // Add the chosen variant straight to the cart, so the customer never loses
    // the look they just built by navigating away.
    if (s.vid && s.purchasable) {
      btn.disabled = true; btn.textContent = 'ADDING…';
      fetch(cart.addUrl || '/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: s.vid, quantity: 1 }] })
      })
        .then(function (r) {
          if (!r.ok) { return r.json().then(function (e) { throw new Error((e && e.description) || 'cart'); }); }
          return r.json();
        })
        .then(function () {
          tracker.track('cart_add', s.id);
          btn.textContent = 'ADDED ✓ VIEW BAG';
          btn.disabled = false;
          btn.onclick = function () { window.location.href = cart.cartUrl || '/cart'; };
          // Let the theme's own cart drawer or bubble know something changed.
          document.documentElement.dispatchEvent(new CustomEvent('nyla:cart:added', {
            bubbles: true, detail: { shade: s.id, variant: s.vid }
          }));
        })
        .catch(function () {
          // Sold out between render and click, or the cart refused it: send them
          // to the product page rather than leave a dead button.
          if (s.url) window.location.href = s.url;
          btn.textContent = 'BUY NOW'; btn.disabled = false;
        });
      return;
    }

    if (s.url) window.location.href = s.url;
  };

  // ── Overlays ──────────────────────────────────────────────────────────────
  TryOn.prototype.show = function (state, msg) {
    this.ovStart.hidden = state !== 'start';
    this.ovLoad.hidden = state !== 'loading';
    this.ovErr.hidden = state !== 'error';
    if (state === 'loading' && msg) this.ovLoad.querySelector('.nyt-load-msg').textContent = msg;
    if (state === 'error' && msg) this.ovErr.querySelector('.nyt-err-msg').textContent = msg;
    this.root.classList.toggle('is-live', state === 'run');
  };

  TryOn.prototype.ensureEngine = function () {
    if (this.engine) return true;
    try {
      this.engine = new NylaMakeup.Engine(this.canvas);
      if (this.cfg.tuning) this.engine.setTuning(this.cfg.tuning);
      return true;
    } catch (e) {
      this.show('error', 'This browser does not support WebGL, which the try-on needs. Try updating your browser.');
      return false;
    }
  };

  TryOn.prototype.warmUp = function () { return getLandmarker(); };

  // ── Camera mode ───────────────────────────────────────────────────────────
  TryOn.prototype.startCamera = function () {
    var self = this;
    if (!this.ensureEngine()) return;
    this.show('loading', 'Setting up the experience…');
    this.stopStream();
    this.mode = 'camera';
    this.btnMode.innerHTML = ICONS.photo; this.btnMode.title = 'Try on a photo';
    this.btnFlip.style.display = '';

    var constraints = { video: { facingMode: this.facing, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false };
    Promise.all([
      navigator.mediaDevices.getUserMedia(constraints),
      // Tagged so the catch below can tell an engine failure from a camera one.
      this.warmUp().catch(function (e) {
        var wrapped = e instanceof Error ? e : new Error(String(e));
        wrapped.__engine = true;
        throw wrapped;
      })
    ]).then(function (r) {
      var stream = r[0];
      self.stream = stream;
      self.video.srcObject = stream;
      self.show('loading', 'Almost there…');
      return self.video.play().then(function () { return setLmMode('VIDEO'); });
    }).then(function () {
      self.running = true;
      self.smooth = null;
      self.fade = 0;
      self.show('run');
      if (!self._camTracked) { self._camTracked = true; tracker.track('camera_start'); }
      self.loop();
    }).catch(function (err) {
      self.running = false;
      var msg = 'Could not start the camera.';
      if (err && err.__engine) msg = 'Could not load the AR engine. Check your internet connection and try again.';
      else if (err && (err.name === 'NotAllowedError' || err.name === 'SecurityError')) msg = 'Camera access was blocked. Allow camera permission for this site and try again (the site must be on HTTPS).';
      else if (err && err.name === 'NotFoundError') msg = 'No camera was found on this device.';
      else if (err && err.name === 'NotReadableError') msg = 'The camera is already in use by another app or tab. Close it and try again.';
      else if (err && String(err.message || err).match(/tasks-vision|fetch|network|import|require/i)) msg = 'Could not load the AR engine. Check your internet connection and try again.';
      tracker.track('camera_error', '', (err && err.name) || 'unknown');
      self.show('error', msg);
    });
  };

  TryOn.prototype.pauseCamera = function () {
    this.running = false;
    this.stopStream();
    if (this.mode === 'camera') this.show('start');
  };

  TryOn.prototype.stopStream = function () {
    if (this.stream) {
      this.stream.getTracks().forEach(function (t) { t.stop(); });
      this.stream = null;
    }
    this.video.srcObject = null;
  };

  // Velocity-adaptive smoothing: steady when still, snaps when moving.
  TryOn.prototype.smoothLm = function (lm, dt) {
    if (!lm) { this.smooth = null; return null; }
    if (!this.smooth || this.smooth.length !== lm.length) {
      this.smooth = lm.map(function (p) { return { x: p.x, y: p.y, z: p.z }; });
      return this.smooth;
    }
    var s = this.smooth, idx = [1, 61, 291, 13, 14], sp = 0;
    for (var j = 0; j < idx.length; j++) {
      var p = lm[idx[j]], q = s[idx[j]];
      sp += Math.abs(p.x - q.x) + Math.abs(p.y - q.y);
    }
    sp /= idx.length;
    var k = Math.max(0.5, Math.min(2.5, dt / 16.7));     // frame-rate independent
    var a = Math.min(1, (0.45 + sp * 55) * k);
    for (var i = 0; i < lm.length; i++) {
      s[i].x += (lm[i].x - s[i].x) * a;
      s[i].y += (lm[i].y - s[i].y) * a;
    }
    return s;
  };

  TryOn.prototype.loop = function () {
    var self = this;
    if (!this.running || this.mode !== 'camera') return;
    var v = this.video;
    var now = performance.now();
    var dt = Math.min(100, now - (this.lastT || now)); this.lastT = now;

    if (v.readyState >= 2 && v.videoWidth) {
      this.engine.setSource(v, v.videoWidth, v.videoHeight);
      this.engine.setMirror(this.facing === 'user');

      var lm = null;
      var detect = !this.skip;
      if (this.detectMs > 45) { this.skip = !this.skip; } else { this.skip = false; }
      if (detect && landmarker) {
        var t0 = performance.now();
        var res = null;
        try { res = landmarker.detectForVideo(v, now); } catch (e) {}
        this.detectMs = this.detectMs * 0.9 + (performance.now() - t0) * 0.1;
        lm = res && res.faceLandmarks && res.faceLandmarks[0] || null;
        this.lastLm = lm;
      } else {
        lm = this.lastLm || null;
      }

      var sm = this.smoothLm(lm, dt);
      var target = sm ? 1 : 0;
      this.fade += (target - this.fade) * Math.min(1, dt / 130);
      if (this.fade < 0.02 && !sm) this.fade = 0;
      this.hint.hidden = !!sm || !(this.sel.lip || this.sel.cheek);

      this.engine.setLandmarks(sm);
      this.engine.setFade(this.fade);
      this.pushProducts();
      this.engine.render();
    }
    if (v.requestVideoFrameCallback && this.detectMs < 30) {
      v.requestVideoFrameCallback(function () { self.loop(); });
    } else {
      requestAnimationFrame(function () { self.loop(); });
    }
  };

  // ── Photo mode ────────────────────────────────────────────────────────────
  TryOn.prototype.loadPhoto = function (file) {
    var self = this, MAX = 1600;
    if (!this.ensureEngine()) return;
    this.show('loading', 'Reading your photo…');
    this.running = false;
    this.stopStream();

    // Draw a decoded image/bitmap to a capped-size canvas for detection + texture.
    function toCanvas(srcEl, natW, natH) {
      var W = natW, H = natH;
      if (Math.max(W, H) > MAX) { var k = MAX / Math.max(W, H); W = Math.round(W * k); H = Math.round(H * k); }
      var c = document.createElement('canvas'); c.width = W; c.height = H;
      c.getContext('2d').drawImage(srcEl, 0, 0, W, H);
      return c;
    }
    function detectAndShow(canvas) {
      self.warmUp().then(function () { return setLmMode('IMAGE'); }).then(function () {
        var res = null; try { res = landmarker.detect(canvas); } catch (e) {}
        var lm = res && res.faceLandmarks && res.faceLandmarks[0] || null;
        if (!lm) { self.show('error', 'No face was detected in that photo. Use a clear, front-facing photo in good light.'); return; }
        self.mode = 'photo'; tracker.track('photo_mode');
        self.imageEl = canvas; self.imageLm = lm;
        self.btnMode.innerHTML = ICONS.cam; self.btnMode.title = 'Back to camera';
        self.btnFlip.style.display = 'none';
        self.fade = 1; self.show('run'); self.repaintStatic(true);
      }).catch(function () { self.show('error', 'Could not process that photo. Try a different one.'); });
    }
    function fallbackImg() {
      var img = new Image();
      img.onload = function () { var c = toCanvas(img, img.naturalWidth, img.naturalHeight); URL.revokeObjectURL(img.src); detectAndShow(c); };
      img.onerror = function () { URL.revokeObjectURL(img.src); self.show('error', "That image couldn't be opened (" + (file.type || 'unknown format') + "). Try a JPG or PNG."); };
      try { img.src = URL.createObjectURL(file); } catch (e) { self.show('error', 'Could not read that file.'); }
    }

    // Preferred path: createImageBitmap decodes HEIC + large photos and honours
    // EXIF orientation (an upright canvas → the face is actually detectable).
    // This is what fixes iOS "image not supported" / sideways photos.
    if (window.createImageBitmap) {
      createImageBitmap(file, { imageOrientation: 'from-image' })
        .then(function (bmp) { var c = toCanvas(bmp, bmp.width, bmp.height); try { bmp.close(); } catch (e) {} detectAndShow(c); })
        .catch(function () {
          // some engines reject the options arg — retry plain, then <img>
          createImageBitmap(file)
            .then(function (bmp) { var c = toCanvas(bmp, bmp.width, bmp.height); try { bmp.close(); } catch (e) {} detectAndShow(c); })
            .catch(fallbackImg);
        });
    } else { fallbackImg(); }
  };

  TryOn.prototype.repaintStatic = function (force) {
    if (this.mode !== 'photo' || !this.imageEl) return;
    this.engine.setSource(this.imageEl, this.imageEl.width, this.imageEl.height);
    this.engine.setMirror(false);
    this.engine.setLandmarks(this.imageLm);
    this.engine.setFade(1);
    this.pushProducts();
    this.engine.render();
  };

  // ── Capture ───────────────────────────────────────────────────────────────
  TryOn.prototype.capture = function () {
    if (!this.engine || !this.root.classList.contains('is-live')) return;
    tracker.track('capture', (this.lastTouched || {}).id);
    var url = this.engine.snapshot();
    var flash = el('div', 'nyt-flash'); this.stage.append(flash);
    setTimeout(function () { flash.remove(); }, 450);

    function dataURLtoBlob(u) {
      var parts = u.split(','), bin = atob(parts[1]), arr = new Uint8Array(bin.length);
      for (var i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      return new Blob([arr], { type: 'image/jpeg' });
    }
    var blob = dataURLtoBlob(url);
    var fileObj = new File([blob], 'nyla-tryon.jpg', { type: 'image/jpeg' });
    if (navigator.canShare && navigator.canShare({ files: [fileObj] })) {
      navigator.share({ files: [fileObj], title: 'NYLA Virtual Try-On' }).catch(function () {});
    } else {
      var a = document.createElement('a');
      a.href = URL.createObjectURL(blob); a.download = 'nyla-tryon.jpg';
      document.body.appendChild(a); a.click(); a.remove();
    }
  };

  // ── Modal management ──────────────────────────────────────────────────────
  function openModal(m) {
    if (!m) return;
    tracker.track('open', '', 'modal');
    m.classList.add('is-open');
    m.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('nyt-noscroll');
    var root = m.querySelector('.nyla-tryon');
    if (root && root.__nyt) {
      root.__nyt.warmUp();
      if (!root.__nyt.running && root.__nyt.mode === 'camera') root.__nyt.show('start');
    }
  }
  function closeModal(m) {
    if (!m) return;
    m.classList.remove('is-open');
    m.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('nyt-noscroll');
    var root = m.querySelector('.nyla-tryon');
    if (root && root.__nyt) {
      var inst = root.__nyt;
      inst.running = false;
      inst.stopStream();
      inst.show('start');
    }
  }

  function bindModals() {
    document.querySelectorAll('.nyt-open-modal').forEach(function (btn) {
      if (btn.__bound) return; btn.__bound = true;
      btn.addEventListener('click', function () {
        var id = btn.getAttribute('data-target');
        openModal(id ? document.getElementById(id) : document.querySelector('.nyt-modal'));
      });
      btn.addEventListener('pointerover', function () {
        var id = btn.getAttribute('data-target');
        var m = id ? document.getElementById(id) : document.querySelector('.nyt-modal');
        var root = m && m.querySelector('.nyla-tryon');
        if (root && root.__nyt) root.__nyt.warmUp();
      }, { once: true });
    });
    document.querySelectorAll('.nyt-modal').forEach(function (m) {
      if (m.__bound) return; m.__bound = true;
      m.addEventListener('click', function (e) {
        if (e.target === m || e.target.classList.contains('nyt-modal-close')) closeModal(m);
      });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') document.querySelectorAll('.nyt-modal.is-open').forEach(closeModal);
    });
  }

  function boot() {
    document.querySelectorAll('.nyla-tryon').forEach(function (elr) {
      if (!elr.__nyt) elr.__nyt = new TryOn(elr);
      if (!elr.closest('.nyt-modal')) {
        var warm = function () { elr.__nyt.warmUp(); };
        if ('requestIdleCallback' in window) requestIdleCallback(warm, { timeout: 3000 });
        else setTimeout(warm, 1500);
      }
    });
    bindModals();
  }
  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);
})();
